import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

/* ---------- types ---------- */
interface Agent {
  id: string;
  name: string;
  type: string;
  model: string;
  description: string;
  status: 'active' | 'draft' | 'error';
  sessions: number;
  lastActive: string;
}

/* ---------- mock data ---------- */
const AGENTS: Agent[] = [
  {
    id: 'emily',
    name: 'Emily',
    type: 'Management',
    model: 'GPT-4o',
    description: 'Restaurant concierge that manages menu queries and reservations through sub-agents.',
    status: 'active',
    sessions: 847,
    lastActive: '2 min ago',
  },
  {
    id: 'emily-menu',
    name: 'Emily-Menu',
    type: 'Conversational',
    model: 'GPT-4o-mini',
    description: 'Handles menu-related questions including dietary needs, pricing, and recommendations.',
    status: 'active',
    sessions: 512,
    lastActive: '42 min ago',
  },
  {
    id: 'emily-reservations',
    name: 'Emily-Reservations',
    type: 'Task',
    model: 'GPT-4o',
    description: 'Manages table bookings, availability checks, and reservation modifications.',
    status: 'error',
    sessions: 335,
    lastActive: '15 min ago',
  },
  {
    id: 'martin',
    name: 'Martin',
    type: 'General',
    model: 'Claude 3.5',
    description: 'General-purpose assistant for HR policies, company information, and employee queries.',
    status: 'active',
    sessions: 203,
    lastActive: '1 hour ago',
  },
  {
    id: 'data-extractor',
    name: 'Data Extractor',
    type: 'Data',
    model: 'GPT-4o',
    description: 'Extracts structured data from documents, invoices, and receipts using OCR and LLM.',
    status: 'draft',
    sessions: 0,
    lastActive: 'Never',
  },
];

const FILTER_TABS = ['All', 'Management', 'Conversational', 'Task', 'General', 'Data'] as const;

/* ---------- styled components ---------- */
const PageBody = styled.div`
  padding: 2rem 0 4rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageHeaderLeft = styled.div`
  h1 {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
  }
  p {
    font-size: 0.875rem;
    color: var(--text-muted);
  }
`;

const PageHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  min-width: 280px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: var(--accent);
  }

  input {
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 0.875rem;
    color: var(--text-primary);
    outline: none;
    flex: 1;

    &::placeholder {
      color: var(--text-muted);
    }
  }
`;

const CreateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: var(--accent);
  color: white;

  &:hover {
    background: var(--accent-hover);
    box-shadow: 0 4px 12px rgba(45, 154, 143, 0.3);
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const FilterTab = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border)')};
  background: ${({ $active }) => ($active ? 'var(--accent-subtle)' : 'var(--bg-surface)')};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-secondary)')};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AgentCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const AgentIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
`;

const StatusDot = styled.span<{ $status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background: ${({ $status }) =>
    $status === 'active'
      ? 'var(--success)'
      : $status === 'error'
        ? 'var(--error)'
        : 'var(--text-muted)'};
`;

const Badge = styled.span<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  background: ${({ $variant }) =>
    $variant === 'teal'
      ? 'var(--accent-subtle)'
      : $variant === 'orange'
        ? 'var(--warning-subtle)'
        : $variant === 'blue'
          ? 'var(--info-subtle)'
          : $variant === 'green'
            ? 'var(--success-subtle)'
            : $variant === 'red'
              ? 'var(--error-subtle)'
              : 'var(--bg-elevated)'};
  color: ${({ $variant }) =>
    $variant === 'teal'
      ? 'var(--accent)'
      : $variant === 'orange'
        ? 'var(--warning)'
        : $variant === 'blue'
          ? 'var(--info)'
          : $variant === 'green'
            ? 'var(--success)'
            : $variant === 'red'
              ? 'var(--error)'
              : 'var(--text-muted)'};
`;

const AgentName = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const AgentModel = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const AgentDesc = styled.p`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0.75rem 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: var(--text-muted);
  padding-top: 0.75rem;
  border-top: 1px solid var(--border);
`;

const NewAgentCard = styled(AgentCard)`
  border-style: dashed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: var(--text-muted);
  font-size: 0.9375rem;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const PlusIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
`;

/* ---------- helpers ---------- */
const getTypeBadgeVariant = (type: string): string => {
  switch (type) {
    case 'Management': return 'teal';
    case 'Conversational': return 'blue';
    case 'Task': return 'orange';
    case 'General': return 'green';
    case 'Data': return 'neutral';
    default: return 'neutral';
  }
};

/* ---------- component ---------- */
const Agents: React.FC = () => {
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const filtered = AGENTS.filter((a) => {
    if (activeTab !== 'All' && a.type !== activeTab) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <PageBody className="fade-in">
      <Container>
        <PageHeader>
          <PageHeaderLeft>
            <h1>Agents</h1>
            <p>Manage and configure your AI agents</p>
          </PageHeaderLeft>
          <PageHeaderRight>
            <SearchBar>
              <span style={{ color: 'var(--text-muted)' }}>&#x1F50D;</span>
              <input
                placeholder="Search agents..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchBar>
            <CreateBtn>&#x2795; New Agent</CreateBtn>
          </PageHeaderRight>
        </PageHeader>

        <FilterTabs>
          {FILTER_TABS.map((tab) => (
            <FilterTab
              key={tab}
              $active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </FilterTab>
          ))}
        </FilterTabs>

        <Grid>
          <NewAgentCard>
            <PlusIcon>+</PlusIcon>
            Create New Agent
          </NewAgentCard>

          {filtered.map((agent) => (
            <AgentCard
              key={agent.id}
              onClick={() => navigate(`/agents/${agent.id}`)}
            >
              <CardTop>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <AgentIcon>{agent.name[0]}</AgentIcon>
                  <div>
                    <AgentName>
                      <StatusDot $status={agent.status} />{' '}
                      {agent.name}
                    </AgentName>
                    <AgentModel>{agent.model}</AgentModel>
                  </div>
                </div>
                <Badge $variant={getTypeBadgeVariant(agent.type)}>
                  {agent.type}
                </Badge>
              </CardTop>
              <AgentDesc>{agent.description}</AgentDesc>
              <CardMeta>
                <span>&#x1F4AC; {agent.sessions} sessions</span>
                <span>&#x1F552; {agent.lastActive}</span>
              </CardMeta>
            </AgentCard>
          ))}
        </Grid>
      </Container>
    </PageBody>
  );
};

export default Agents;
